"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    eurosPrecis,
    remplir,
    useParcours,
} from "./Journey";
import { BAREME_IK, indemnites } from "@/lib/tools/mileageAllowance";
import { coutPourNet } from "@/lib/tools/monthlyPayout";
import { expliquerIndemnitesKilometriques } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Indemnités kilométriques : combien, et qui peut s'en servir.
 *
 * LE MÊME BARÈME NE DONNE PAS LE MÊME DROIT À TOUT LE MONDE. En micro, il ne
 * sert à rien : l'abattement couvre déjà les déplacements. En entreprise
 * individuelle au BIC, il n'est pas admis. En BNC, il se déduit du bénéfice.
 * En société, c'est la société qui rembourse, et une indemnité bien justifiée
 * n'est pas un revenu. L'écran demande donc le statut d'abord, et refuse
 * d'afficher un montant à qui ne peut pas s'en servir.
 *
 * LE CHIFFRE QUI FAIT COMPRENDRE, EN SASU : ce que la société devrait
 * dépenser pour mettre la même somme dans la poche de son président par un
 * salaire. Il sort du même calcul que « Combien me verser ce mois-ci ».
 *
 * CE QUE LES SOURCES NE TRANCHENT PAS, L'ÉCRAN NE LE TRANCHE PAS. Les
 * cotisations sur les indemnités d'un gérant majoritaire d'EURL, et le sort
 * des indemnités dans une EURL restée à l'impôt sur le revenu, n'ont trouvé
 * aucune source officielle au relevé du 24 septembre 2026 : l'écran le dit.
 */

const PHASES = {
    statut: 0,
    ei: 0,
    eurl: 0,
    refus: 0,
    vehicule: 1,
    detail: 1,
    km: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Statut", "Véhicule", "Kilomètres"],
        retour: "Retour",
        statut: {
            titre: "Sous quel statut travaillez-vous ?",
            aide: "Le barème est le même pour tous, mais ce que vous avez le droit d'en faire dépend de votre statut.",
            choix: [
                { cle: "micro", icone: "case-minimalistic-linear", titre: "Micro-entreprise", detail: "Vous déclarez votre chiffre d'affaires à l'Urssaf." },
                { cle: "ei", icone: "user-id-linear", titre: "Entreprise individuelle au réel", detail: "Vous déclarez un bénéfice, recettes moins dépenses." },
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL", detail: "Vous êtes gérant et seul associé." },
                { cle: "sasu", icone: "user-check-linear", titre: "SASU", detail: "Vous êtes président et seul associé." },
            ],
        },
        ei: {
            titre: "Votre bénéfice relève-t-il des BIC ou des BNC ?",
            aide: "C'est écrit sur votre déclaration de résultats : 2031 pour les BIC, 2035 pour les BNC.",
            choix: [
                { cle: "bnc", icone: "pen-new-square-linear", titre: "BNC", detail: "Activité libérale : conseil, formation, développement, design." },
                { cle: "bic", icone: "shop-linear", titre: "BIC", detail: "Commerce, artisanat, prestations commerciales." },
            ],
        },
        eurl: {
            titre: "Votre EURL a-t-elle choisi l'impôt sur les sociétés ?",
            aide: "Sans option, une EURL dont l'associé est une personne physique reste à l'impôt sur le revenu.",
            choix: [
                { cle: "eurl", icone: "safe-square-linear", titre: "Oui, elle est à l'IS", detail: "Vous vous versez une rémunération de gérant." },
                { cle: "eurlIr", icone: "document-text-linear", titre: "Non, elle est à l'impôt sur le revenu", detail: "Son bénéfice est imposé directement à votre nom." },
            ],
        },
        refus: {
            micro: {
                titre: "En micro, vous ne pouvez pas déduire d'indemnités kilométriques",
                aide: "Votre abattement forfaitaire couvre déjà toutes vos dépenses, frais de déplacement compris : vous ne pouvez rien déduire en plus.",
                points: [
                    "Si vous roulez beaucoup pour vos clients, vos frais dépassent peut-être votre abattement : regardez ce que vous garderiez au régime réel.",
                ],
                lien: "Quand quitter la micro-entreprise",
            },
            bic: {
                titre: "Au BIC, le barème kilométrique n'est pas admis",
                aide: "L'administration le réserve aux BNC. Au BIC, vous déduisez vos frais de voiture pour leur montant réel : carburant, entretien, assurance, amortissement, au prorata de l'usage professionnel.",
                points: [
                    "Avec la comptabilité super-simplifiée, vous pouvez aussi évaluer le carburant au barème carburant publié chaque année.",
                    "Gardez toutes vos factures et un relevé de vos trajets : ce sont eux qui justifient la part professionnelle.",
                ],
            },
            eurlIr: {
                titre: "Pour une EURL à l'impôt sur le revenu, nous n'avons pas trouvé de règle officielle",
                aide: "Les textes et la doctrine que nous avons relus traitent des sociétés à l'impôt sur les sociétés et des entrepreneurs individuels, pas de ce cas. Plutôt qu'un montant qui pourrait être refusé, nous préférons ne rien afficher.",
                points: [
                    "Posez la question à votre expert-comptable : il vous dira si vos déplacements se déduisent au réel ou au barème.",
                ],
            },
        },
        vehicule: {
            titre: "Avec quel véhicule vous déplacez-vous ?",
            aide: "Un barème par véhicule. Si vous en utilisez deux, faites le calcul pour chacun.",
            choix: [
                { cle: "voiture", icone: "car-linear", titre: "Une voiture", detail: "Y compris hybride, rechargeable ou non." },
                { cle: "moto", icone: "scooter-linear", titre: "Une moto", detail: "Plus de 50 cm³, ou plus de 4 kW en électrique." },
                { cle: "cyclomoteur", icone: "kick-scooter-linear", titre: "Un cyclomoteur", detail: "50 cm³ au plus, 45 km/h au plus." },
            ],
        },
        detail: {
            titre: "Votre véhicule",
            aide: "La puissance fiscale est sur la carte grise, case P.6.",
            motorisation: "Motorisation",
            thermique: "Essence, diesel ou hybride",
            electrique: "100 % électrique",
            puissance: "Puissance fiscale",
            puissances: {
                voiture: { 3: "3 CV et moins", 4: "4 CV", 5: "5 CV", 6: "6 CV", 7: "7 CV et plus" },
                moto: { "1-2": "1 ou 2 CV", "3-5": "3 à 5 CV", "5+": "Plus de 5 CV" },
            },
            suivant: "Continuer",
        },
        km: {
            titre: "Combien de kilomètres professionnels faites-vous par an ?",
            aide: "Vos trajets vers vos clients, vos fournisseurs, vos formations. Pour aller à votre bureau, seuls les 40 premiers kilomètres comptent, sauf raison particulière.",
            libelle: "Kilomètres professionnels par an",
            suivant: "Voir mes indemnités",
        },
        resultat: {
            titre: "Vos indemnités kilométriques",
            montant: "{montant} par an",
            formule: "Le calcul : {formule}. Soit {parKm} par kilomètre en moyenne.",
            sasu: {
                titre: "Votre SASU vous les rembourse",
                texte: "Pour vous, ce n'est pas un revenu : pas d'impôt sur le revenu, tant que vos kilomètres sont exacts et justifiés. Pour votre société, c'est une charge, qui baisse son impôt sur les sociétés.",
                comparaison: "Pour mettre {montant} dans votre poche par un salaire, votre société devrait dépenser {cout}.",
                cotisations: "Côté cotisations, le Bulletin officiel de la sécurité sociale admet ces indemnités sans cotisations pour un président de SAS, dans la limite du barème.",
            },
            eurl: {
                titre: "Votre EURL vous les rembourse",
                texte: "Pour vous, ce n'est pas un revenu imposable, tant que vos kilomètres sont exacts et justifiés : ce sont des frais réels. Pour votre société, c'est une charge, qui baisse son impôt sur les sociétés.",
                cotisations: "Côté cotisations, aucune source officielle ne dit si ces remboursements entrent dans l'assiette d'un gérant majoritaire. Demandez à votre expert-comptable comment il les traite.",
            },
            bnc: {
                titre: "Vous les déduisez de votre bénéfice",
                texte: "Ce montant vient en déduction de vos recettes, à la place de vos vrais frais de voiture : il baisse à la fois vos cotisations et votre impôt.",
                option: "C'est une option : elle se prend pour l'année entière, au 1er janvier, et vaut pour tous vos véhicules. Vous ne déduisez alors plus ni carburant, ni entretien, ni assurance, ni amortissement.",
            },
            enPlus: {
                titre: "Ce que vous pouvez ajouter",
                items: ["vos péages ;", "vos frais de parking et de stationnement, sauf votre propre garage ;", "les intérêts du crédit de votre voiture, au prorata de l'usage professionnel."],
            },
            dedans: "Le carburant, la recharge, l'entretien, les pneus, l'assurance et la perte de valeur du véhicule sont déjà dans le barème : ne les comptez pas une deuxième fois.",
            justificatifs: {
                titre: "Ce qu'il faut garder",
                items: [
                    "la carte grise, à votre nom ou à celui de votre foyer ;",
                    "un relevé de chaque déplacement : la date, le client ou le lieu, les kilomètres ;",
                    "vos tickets de péage et de parking.",
                ],
            },
            lienVerser: "Combien me verser ce mois-ci",
            avertissement:
                "Le barème est celui publié en 2023 et reconduit depuis. Le barème des revenus 2026 n'est pas encore paru : s'il change, les montants changeront avec lui.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Status", "Vehicle", "Kilometres"],
        retour: "Back",
        statut: {
            titre: "Under which status do you work?",
            aide: "The scale is the same for everyone, but what you are allowed to do with it depends on your status.",
            choix: [
                { cle: "micro", icone: "case-minimalistic-linear", titre: "Micro-entreprise", detail: "You declare your turnover to the Urssaf." },
                { cle: "ei", icone: "user-id-linear", titre: "Sole trader on actual expenses", detail: "You declare a profit, takings minus expenses." },
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL", detail: "You are the manager and only shareholder." },
                { cle: "sasu", icone: "user-check-linear", titre: "SASU", detail: "You are the president and only shareholder." },
            ],
        },
        ei: {
            titre: "Does your profit fall under BIC or BNC?",
            aide: "It is written on your tax return: 2031 for BIC, 2035 for BNC.",
            choix: [
                { cle: "bnc", icone: "pen-new-square-linear", titre: "BNC", detail: "Liberal profession: consulting, training, development, design." },
                { cle: "bic", icone: "shop-linear", titre: "BIC", detail: "Trade, crafts, commercial services." },
            ],
        },
        eurl: {
            titre: "Has your EURL chosen corporate tax?",
            aide: "Without the option, an EURL whose shareholder is an individual stays under income tax.",
            choix: [
                { cle: "eurl", icone: "safe-square-linear", titre: "Yes, it is under corporate tax", detail: "You pay yourself as a manager." },
                { cle: "eurlIr", icone: "document-text-linear", titre: "No, it is under income tax", detail: "Its profit is taxed directly in your name." },
            ],
        },
        refus: {
            micro: {
                titre: "Under the micro-entreprise, you cannot deduct mileage allowances",
                aide: "Your flat-rate allowance already covers all your expenses, travel included: you cannot deduct anything on top.",
                points: [
                    "If you drive a lot for your clients, your costs may exceed your allowance: see what you would keep under an actual-expenses regime.",
                ],
                lien: "When to leave the micro-entreprise",
            },
            bic: {
                titre: "Under BIC, the mileage scale is not allowed",
                aide: "The tax office reserves it for BNC. Under BIC, you deduct your car costs at their actual amount: fuel, servicing, insurance, depreciation, pro rata to business use.",
                points: [
                    "With super-simplified bookkeeping, you can also value fuel with the fuel scale published every year.",
                    "Keep all your invoices and a log of your trips: they justify the business share.",
                ],
            },
            eurlIr: {
                titre: "For an EURL under income tax, we found no official rule",
                aide: "The texts and guidance we reread cover companies under corporate tax and sole traders, not this case. Rather than an amount that could be refused, we prefer to show nothing.",
                points: [
                    "Ask your accountant: they will tell you whether your trips are deducted at actual cost or with the scale.",
                ],
            },
        },
        vehicule: {
            titre: "Which vehicle do you travel with?",
            aide: "One scale per vehicle. If you use two, run the calculation for each.",
            choix: [
                { cle: "voiture", icone: "car-linear", titre: "A car", detail: "Hybrids included, plug-in or not." },
                { cle: "moto", icone: "scooter-linear", titre: "A motorbike", detail: "Over 50 cc, or over 4 kW if electric." },
                { cle: "cyclomoteur", icone: "kick-scooter-linear", titre: "A moped", detail: "50 cc at most, 45 km/h at most." },
            ],
        },
        detail: {
            titre: "Your vehicle",
            aide: "The fiscal horsepower is on the registration certificate, box P.6.",
            motorisation: "Engine",
            thermique: "Petrol, diesel or hybrid",
            electrique: "100% electric",
            puissance: "Fiscal horsepower",
            puissances: {
                voiture: { 3: "3 CV or less", 4: "4 CV", 5: "5 CV", 6: "6 CV", 7: "7 CV or more" },
                moto: { "1-2": "1 or 2 CV", "3-5": "3 to 5 CV", "5+": "Over 5 CV" },
            },
            suivant: "Continue",
        },
        km: {
            titre: "How many business kilometres do you drive a year?",
            aide: "Your trips to clients, suppliers, training. To reach your own office, only the first 40 kilometres count, unless there is a particular reason.",
            libelle: "Business kilometres a year",
            suivant: "See my allowances",
        },
        resultat: {
            titre: "Your mileage allowances",
            montant: "{montant} a year",
            formule: "The calculation: {formule}. That is {parKm} per kilometre on average.",
            sasu: {
                titre: "Your SASU reimburses them",
                texte: "For you, it is not income: no income tax, as long as your kilometres are exact and justified. For your company, it is an expense, which lowers its corporate tax.",
                comparaison: "To put {montant} in your pocket through a salary, your company would have to spend {cout}.",
                cotisations: "On contributions, the official social security bulletin accepts these allowances without contributions for a SAS president, within the scale.",
            },
            eurl: {
                titre: "Your EURL reimburses them",
                texte: "For you, it is not taxable income, as long as your kilometres are exact and justified: they are actual expenses. For your company, it is an expense, which lowers its corporate tax.",
                cotisations: "On contributions, no official source says whether these reimbursements count towards a majority manager's contribution base. Ask your accountant how they treat them.",
            },
            bnc: {
                titre: "You deduct them from your profit",
                texte: "This amount is deducted from your takings, instead of your real car costs: it lowers both your contributions and your income tax.",
                option: "It is an option: it is taken for the whole year, from 1 January, and covers all your vehicles. You then no longer deduct fuel, servicing, insurance or depreciation.",
            },
            enPlus: {
                titre: "What you can add",
                items: ["your tolls;", "your parking costs, except your own garage;", "the interest on your car loan, pro rata to business use."],
            },
            dedans: "Fuel, charging, servicing, tyres, insurance and the vehicle's loss of value are already in the scale: do not count them twice.",
            justificatifs: {
                titre: "What to keep",
                items: [
                    "the registration certificate, in your name or your household's;",
                    "a log of each trip: the date, the client or place, the kilometres;",
                    "your toll and parking receipts.",
                ],
            },
            lienVerser: "How much to pay myself this month",
            avertissement:
                "The scale is the one published in 2023 and carried over since. The scale for 2026 income has not been published yet: if it changes, the amounts will change with it.",
            recommencer: "Run the calculation again",
        },
    },
};

/** Un coefficient du barème, lu comme il est publié : « 0,357 ». */
function coefficientLisible(c, lang) {
    return c.toLocaleString(lang === "fr" ? "fr-FR" : "en-GB", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export default function IndemnitesKilometriques({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : le statut part déjà posé, et le
    // parcours s'ouvre sur la première question qui manque (le véhicule), ou
    // directement sur le refus motivé quand le statut n'y ouvre pas droit.
    const { valeurs: dossier } = useDossierOutil();
    const [statut, setStatut] = useState(dossier.statut ?? null);
    const [vehicule, setVehicule] = useState(null);
    const [electrique, setElectrique] = useState(false);
    const [puissance, setPuissance] = useState("5");
    const [km, setKm] = useState(8000);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "statut",
        () => {
            setStatut(null);
            setVehicule(null);
            setElectrique(false);
            setPuissance("5");
            setKm(8000);
        },
        PHASES,
        reprendre([
            { etape: "statut", connu: dossier.statut !== undefined },
            // Les deux sous-questions : BNC ou BIC au réel, IS ou IR en EURL.
            ...(dossier.statut === "bnc" || dossier.statut === "bic" ? [{ etape: "ei", connu: true }] : []),
            ...(dossier.statut === "eurl" || dossier.statut === "eurlIr" ? [{ etape: "eurl", connu: true }] : []),
            ...(["micro", "bic", "eurlIr"].includes(dossier.statut)
                ? [{ etape: "refus" }]
                : [
                      // Le véhicule ne se déduit pas du dossier : le parcours s'y arrête.
                      { etape: "vehicule", connu: false },
                      { etape: "resultat" },
                  ]),
        ]),
    );

    useSignalerDossier({ statut }, etape === "resultat");

    const calcul = useMemo(
        () => (etape === "resultat" ? indemnites({ vehicule, puissance, electrique, km }) : null),
        [etape, vehicule, puissance, electrique, km],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(v, lang);
    // Le montant dit d'où il vient : la tranche du barème et sa source.
    const { ouverte, basculer } = useExplications();
    const lienOutil = (fr, en, libelle, type = "primary") => (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: lang === "fr" ? fr : en } }}>
            <Button type={type} className="press-safe w-full">
                {libelle} →
            </Button>
        </Link>
    );
    const choisir = (cle) => {
        setStatut(cle);
        aller(["micro", "bic", "eurlIr"].includes(cle) ? "refus" : "vehicule");
    };

    if (etape === "statut") {
        return (
            <Question
                key="statut"
                {...communsEcran}
                phase={PHASES.statut}
                rang={rang}
                titre={t.statut.titre}
                aide={t.statut.aide}
                choix={t.statut.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "ei") aller("ei");
                        else if (c.cle === "eurl") aller("eurl");
                        else choisir(c.cle);
                    },
                }))}
            />
        );
    }

    if (etape === "ei" || etape === "eurl") {
        const texte = t[etape];
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                rang={rang}
                titre={texte.titre}
                aide={texte.aide}
                choix={texte.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => choisir(c.cle),
                }))}
            />
        );
    }

    if (etape === "refus") {
        const texte = t.refus[statut];
        return (
            <Ecran key="refus" {...communsEcran} phase={PHASES.refus} rang={rang} titre={texte.titre} aide={texte.aide}>
                <Liste items={texte.points} />
                {statut === "micro" && (
                    <div className="mt-5">
                        {lienOutil("quand-quitter-la-micro-entreprise", "when-to-leave-the-micro-entreprise", texte.lien)}
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "vehicule") {
        return (
            <Question
                key="vehicule"
                {...communsEcran}
                phase={PHASES.vehicule}
                rang={rang}
                titre={t.vehicule.titre}
                aide={t.vehicule.aide}
                choix={t.vehicule.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setVehicule(c.cle);
                        setPuissance(c.cle === "voiture" ? "5" : c.cle === "moto" ? "3-5" : "unique");
                        aller("detail");
                    },
                }))}
            />
        );
    }

    if (etape === "detail") {
        const options = t.detail.puissances[vehicule];
        return (
            <Ecran key="detail" {...communsEcran} phase={PHASES.detail} rang={rang} titre={t.detail.titre} aide={options ? t.detail.aide : undefined}>
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.detail.motorisation}</div>
                        <Segments
                            options={[
                                { cle: "thermique", libelle: t.detail.thermique },
                                { cle: "electrique", libelle: t.detail.electrique },
                            ]}
                            valeur={electrique ? "electrique" : "thermique"}
                            poser={(v) => setElectrique(v === "electrique")}
                        />
                    </div>
                    {options && (
                        <div>
                            <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.detail.puissance}</div>
                            <Segments
                                options={BAREME_IK[vehicule].puissances.map((p) => ({ cle: p, libelle: options[p] }))}
                                valeur={puissance}
                                poser={setPuissance}
                            />
                        </div>
                    )}
                </div>
                <Avancer onClick={() => aller("km")}>{t.detail.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "km") {
        return (
            <Ecran key="km" {...communsEcran} phase={PHASES.km} rang={rang} titre={t.km.titre} aide={t.km.aide}>
                <Curseur
                    libelle={t.km.libelle}
                    valeur={km}
                    poser={setKm}
                    minimum={0}
                    maximum={vehicule === "voiture" ? 40000 : 15000}
                    saisie
                    pas={100}
                    formater={(v) => `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB")} km`}
                />
                <Avancer onClick={() => aller("resultat")}>{t.km.suivant}</Avancer>
            </Ecran>
        );
    }

    const c = calcul;
    const nombre = (v) => Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB");
    const formule = c.fixe
        ? `(${nombre(c.km)} × ${coefficientLisible(c.coefficient, lang)}) + ${nombre(c.fixe)}`
        : `${nombre(c.km)} × ${coefficientLisible(c.coefficient, lang)}`;
    const cout = statut === "sasu" ? coutPourNet({ forme: "sasu", net: c.montant }).cout : 0;
    const explication = expliquerIndemnitesKilometriques(c, { vehicule }, lang).montant;
    const bloc = r[statut];

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-3xl leading-tight dark:text-white">
                <MontantExplicable explication={explication} ouvert={ouverte === "montant"} onBasculer={() => basculer("montant")}>
                    <Montant valeur={Math.round(c.montant)} lang={lang} reduit={reduit} />
                </MontantExplicable>
                <span className="text-lg font-semibold text-gray-500 dark:text-lightBlue/70">
                    {" "}
                    {remplir(r.montant, { montant: "" }).trim()}
                </span>
            </div>
            {ouverte === "montant" && <PanneauExplication explication={explication} lang={lang} />}
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                {remplir(r.formule, { formule, parKm: eurosPrecis(c.parKm, lang) })}
            </p>
            <IndiceExplication lang={lang} />

            <div className="mt-6 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-4">
                <div className="flex items-center gap-2 font-semibold dark:text-white">
                    <SolarIcon name="car-linear" className="w-5 h-5 text-primary" />
                    {bloc.titre}
                </div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{bloc.texte}</p>
                {statut === "sasu" && (
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(r.sasu.comparaison, { montant: sommes(c.montant), cout: sommes(cout) })}
                    </p>
                )}
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {statut === "bnc" ? r.bnc.option : bloc.cotisations}
                </p>
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.enPlus.titre}</div>
                <Liste items={r.enPlus.items} />
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{r.dedans}</p>
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.justificatifs.titre}</div>
                <Liste items={r.justificatifs.items} />
            </div>

            {(statut === "sasu" || statut === "eurl") && (
                <div className="mt-6">
                    {lienOutil("combien-me-verser-ce-mois-ci", "how-much-to-pay-yourself-this-month", r.lienVerser, "secondary")}
                </div>
            )}

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">{r.avertissement}</p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
